import { useEffect, useId, useMemo, useState, type ReactNode } from 'react';
import { percent } from './format';

// fixed order: stable colours
const SERIES_VARS = [
  'var(--series-1)',
  'var(--series-2)',
  'var(--series-3)',
  'var(--series-4)',
  'var(--series-5)',
  'var(--series-6)',
  'var(--series-7)',
  'var(--series-8)',
] as const;

export function seriesColor(index: number): string {
  return SERIES_VARS[index % SERIES_VARS.length];
}

interface Margin {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

const DEFAULT_MARGIN: Margin = { top: 14, right: 12, bottom: 28, left: 64 };

function niceTicks(max: number, count = 4): number[] {
  if (max <= 0) return [0, 1];
  const rough = max / count;
  const magnitude = 10 ** Math.floor(Math.log10(rough));
  const normalized = rough / magnitude;
  const step = (normalized >= 5 ? 10 : normalized >= 2 ? 5 : normalized >= 1 ? 2 : 1) * magnitude;
  const ticks = [0];
  // top tick covers max
  while (ticks[ticks.length - 1] < max) ticks.push(Number((ticks[ticks.length - 1] + step).toPrecision(12)));
  return ticks;
}

function Legend({ items }: { items: Array<{ key: string; label: ReactNode; color: string }> }) {
  return (
    <div className="chart-legend" style={{ marginBottom: 12 }}>
      {items.map((item) => (
        <span className="legend-item" key={item.key}>
          <span className="legend-swatch" style={{ background: item.color }} />
          {item.label}
        </span>
      ))}
    </div>
  );
}

function Grid({ ticks, max, width, innerH, margin, formatValue }: { ticks: number[]; max: number; width: number; innerH: number; margin: Margin; formatValue: (v: number) => string }) {
  return (
    <>
      {ticks.map((tick) => {
        const y = margin.top + innerH - (tick / max) * innerH;
        return (
          <g key={tick}>
            <line x1={margin.left} x2={width - margin.right} y1={y} y2={y} stroke="var(--grid)" strokeWidth={1} strokeDasharray={tick === 0 ? undefined : '2 4'} />
            <text x={margin.left - 10} y={y + 3.5} textAnchor="end" fontSize={10} fill="var(--text-muted)">
              {formatValue(tick)}
            </text>
          </g>
        );
      })}
    </>
  );
}

function useMeasuredWidth(fallback = 720) {
  const [node, setNode] = useState<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(fallback);
  useEffect(() => {
    if (!node) return;
    const update = () => setWidth(Math.max(260, Math.round(node.getBoundingClientRect().width)));
    update();
    const observer = new ResizeObserver(update);
    observer.observe(node);
    return () => observer.disconnect();
  }, [node]);
  return [setNode, width] as const;
}

/** Evenly spaced x labels. */
function showLabel(index: number, count: number, innerW: number): boolean {
  const stride = Math.max(1, Math.ceil(count / Math.max(2, Math.floor(innerW / 70))));
  if (index % stride === 0) return index === count - 1 || count - 1 - index >= stride * 0.6 || index === 0;
  if (index !== count - 1) return false;
  return (count - 1) % stride >= stride * 0.6;
}

interface SeriesSpec<T> {
  key: string;
  label: string;
  value: (row: T) => number;
  color?: string;
}

interface TimeSeriesProps<T> {
  data: T[];
  xLabel: (row: T) => string;
  series: SeriesSpec<T>[];
  height?: number;
  formatValue?: (value: number) => string;
  emptyMessage?: string;
  area?: boolean;
}

export function TimeSeriesChart<T>({
  data,
  xLabel,
  series,
  height = 220,
  formatValue = (v) => String(Math.round(v)),
  emptyMessage = 'Belum ada data di rentang ini',
  area = true,
}: TimeSeriesProps<T>) {
  const [hover, setHover] = useState<number | null>(null);
  const gradientId = useId().replace(/:/g, '');
  const [measure, width] = useMeasuredWidth();
  const margin = DEFAULT_MARGIN;
  const innerW = width - margin.left - margin.right;
  const innerH = height - margin.top - margin.bottom;

  const { max, ticks, points } = useMemo(() => {
    const allValues = data.flatMap((row) => series.map((s) => s.value(row)));
    const tickValues = niceTicks(Math.max(...allValues, 0));
    const scaleMax = tickValues[tickValues.length - 1] || 1;
    const xFor = (index: number) => (data.length <= 1 ? margin.left + innerW / 2 : margin.left + (index / (data.length - 1)) * innerW);
    const yFor = (value: number) => margin.top + innerH - (value / scaleMax) * innerH;
    return {
      max: scaleMax,
      ticks: tickValues,
      points: series.map((s, i) => ({
        spec: s,
        color: s.color ?? seriesColor(i),
        coords: data.map((row, index) => ({ x: xFor(index), y: yFor(s.value(row)), v: s.value(row) })),
      })),
    };
  }, [data, series, innerW, innerH, margin.left, margin.top]);

  if (data.length === 0) return <div className="chart-empty">{emptyMessage}</div>;

  const onMove = (event: React.MouseEvent<SVGSVGElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * width;
    if (x < margin.left - 8 || x > width - margin.right + 8) return setHover(null);
    const ratio = data.length <= 1 ? 0 : (x - margin.left) / innerW;
    setHover(Math.max(0, Math.min(data.length - 1, Math.round(ratio * (data.length - 1)))));
  };

  const baseline = margin.top + innerH;

  return (
    <div style={{ position: 'relative' }} ref={measure}>
      {series.length > 1 && <Legend items={points.map((p) => ({ key: p.spec.key, label: p.spec.label, color: p.color }))} />}

      <svg
        className="chart-svg"
        viewBox={`0 0 ${width} ${height}`}
        style={{ width: '100%', height, display: 'block', overflow: 'visible' }}
        onMouseMove={onMove}
        onMouseLeave={() => setHover(null)}
        role="img"
        aria-label={series.map((s) => s.label).join(', ')}
      >
        <defs>
          {points.map((entry, i) => (
            <linearGradient key={entry.spec.key} id={`${gradientId}-${i}`} x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor={entry.color} stopOpacity={0.28} />
              <stop offset="100%" stopColor={entry.color} stopOpacity={0} />
            </linearGradient>
          ))}
        </defs>

        <Grid ticks={ticks} max={max} width={width} innerH={innerH} margin={margin} formatValue={formatValue} />

        {hover !== null && (
          <line x1={points[0].coords[hover].x} x2={points[0].coords[hover].x} y1={margin.top} y2={baseline} stroke="var(--border-strong)" strokeWidth={1} strokeDasharray="3 3" />
        )}

        {points.map((entry, seriesIndex) => {
          const path = entry.coords.map((c, i) => `${i === 0 ? 'M' : 'L'}${c.x},${c.y}`).join(' ');
          const fill = `${path} L${entry.coords[entry.coords.length - 1].x},${baseline} L${entry.coords[0].x},${baseline} Z`;
          return (
            <g key={entry.spec.key}>
              {area && entry.coords.length > 1 && <path d={fill} fill={`url(#${gradientId}-${seriesIndex})`} />}
              <path d={path} fill="none" stroke={entry.color} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
              {entry.coords.length === 1 && <circle cx={entry.coords[0].x} cy={entry.coords[0].y} r={4} fill={entry.color} />}
              {hover !== null && (
                <circle cx={entry.coords[hover].x} cy={entry.coords[hover].y} r={4.5} fill={entry.color} stroke="var(--surface-1)" strokeWidth={2} />
              )}
            </g>
          );
        })}

        <line x1={margin.left} x2={width - margin.right} y1={baseline} y2={baseline} stroke="var(--axis)" strokeWidth={1} />

        {data.map((row, index) => {
          if (!showLabel(index, data.length, innerW)) return null;
          return (
            <text key={index} x={points[0].coords[index].x} y={height - 8} textAnchor="middle" fontSize={10} fill="var(--text-muted)">
              {xLabel(row)}
            </text>
          );
        })}
      </svg>

      {hover !== null && (
        <div
          className="tooltip"
          style={{
            left: `${(points[0].coords[hover].x / width) * 100}%`,
            top: series.length > 1 ? 30 : 0,
            transform: points[0].coords[hover].x > width * 0.6 ? 'translate(-105%, 0)' : 'translate(12px, 0)',
          }}
        >
          <div className="tooltip-title">{xLabel(data[hover])}</div>
          {points.map((p) => (
            <div className="tooltip-row" key={p.spec.key}>
              <span className="legend-swatch" style={{ background: p.color }} />
              {p.spec.label}
              <span className="tooltip-value">{formatValue(p.spec.value(data[hover]))}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

interface BarDatum {
  label: string;
  value: number;
  sublabel?: string;
  icon?: ReactNode;
}

export function RankedBarChart({
  data,
  formatValue = (v) => String(Math.round(v)),
  maxRows = 8,
  colorIndex = 0,
  emptyMessage = 'Belum ada data di rentang ini',
  multicolor = false,
}: {
  data: BarDatum[];
  formatValue?: (value: number) => string;
  maxRows?: number;
  colorIndex?: number;
  emptyMessage?: string;
  multicolor?: boolean;
}) {
  const rows = useMemo(() => [...data].sort((a, b) => b.value - a.value).slice(0, maxRows), [data, maxRows]);
  if (rows.length === 0) return <div className="chart-empty">{emptyMessage}</div>;

  const max = Math.max(...rows.map((r) => r.value)) || 1;
  const total = rows.reduce((sum, r) => sum + r.value, 0) || 1;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {rows.map((row, index) => {
        const color = seriesColor(multicolor ? index : colorIndex);
        return (
          <div key={`${row.label}-${index}`} className="row" style={{ gap: 12, alignItems: 'center' }}>
            {row.icon}
            <div className="grow">
              <div className="row between" style={{ marginBottom: 6, gap: 12 }}>
                <span className="truncate" style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>
                  {row.label}
                  {row.sublabel && <span className="muted" style={{ fontWeight: 400, fontSize: 12 }}> · {row.sublabel}</span>}
                </span>
                <span className="tabular nowrap" style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>
                  {formatValue(row.value)}
                  <span className="muted" style={{ marginLeft: 8, fontSize: 12, fontWeight: 400 }}>{percent((row.value / total) * 100)}</span>
                </span>
              </div>
              <div className="meter">
                <span style={{ width: `${Math.max((row.value / max) * 100, 1.5)}%`, background: color }} />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

interface StackedDatum {
  label: string;
  segments: { key: string; label: string; value: number }[];
}

export function StackedBarChart({
  data,
  height = 240,
  formatValue = (v) => String(Math.round(v)),
  emptyMessage = 'Belum ada data di rentang ini',
  colorFor,
  legend = true,
}: {
  data: StackedDatum[];
  height?: number;
  formatValue?: (value: number) => string;
  emptyMessage?: string;
  colorFor?: (key: string, index: number) => string;
  legend?: boolean;
}) {
  const [hover, setHover] = useState<number | null>(null);
  const [measure, width] = useMeasuredWidth();

  const keys = useMemo(() => {
    const seen = new Map<string, string>();
    for (const row of data) for (const s of row.segments) if (!seen.has(s.key)) seen.set(s.key, s.label);
    return [...seen.entries()].map(([key, label]) => ({ key, label }));
  }, [data]);
  const color = (key: string) => {
    const index = keys.findIndex((k) => k.key === key);
    return colorFor ? colorFor(key, index) : seriesColor(index);
  };

  const totals = data.map((d) => d.segments.reduce((sum, s) => sum + s.value, 0));
  if (data.length === 0 || totals.every((t) => t === 0)) return <div className="chart-empty">{emptyMessage}</div>;

  const margin = DEFAULT_MARGIN;
  const innerW = width - margin.left - margin.right;
  const innerH = height - margin.top - margin.bottom;
  const ticks = niceTicks(Math.max(...totals, 0));
  const max = ticks[ticks.length - 1] || 1;
  const slot = innerW / data.length;
  const barWidth = Math.max(Math.min(slot * 0.68, 40), 2);
  const baseline = margin.top + innerH;

  return (
    <div style={{ position: 'relative' }} ref={measure}>
      {legend && keys.length > 1 && <Legend items={keys.map((k) => ({ key: k.key, label: k.label, color: color(k.key) }))} />}

      <svg
        className="chart-svg"
        viewBox={`0 0 ${width} ${height}`}
        style={{ width: '100%', height, display: 'block', overflow: 'visible' }}
        onMouseLeave={() => setHover(null)}
        role="img"
        aria-label={keys.map((s) => s.label).join(', ')}
      >
        <Grid ticks={ticks} max={max} width={width} innerH={innerH} margin={margin} formatValue={formatValue} />

        {data.map((row, index) => {
          const cx = margin.left + slot * index + slot / 2;
          let cursor = baseline;
          return (
            <g key={`${row.label}-${index}`} onMouseEnter={() => setHover(index)}>
              <rect x={cx - slot / 2} y={margin.top} width={slot} height={innerH} fill={hover === index ? 'var(--accent-dim)' : 'transparent'} />
              {row.segments.map((segment) => {
                const h = (segment.value / max) * innerH;
                if (h <= 0) return null;
                const drawn = Math.max(h - 1.5, 1);
                cursor -= h;
                return (
                  <rect
                    key={segment.key}
                    x={cx - barWidth / 2}
                    y={cursor}
                    width={barWidth}
                    height={drawn}
                    rx={Math.min(3, barWidth / 4)}
                    fill={color(segment.key)}
                    opacity={hover === null || hover === index ? 1 : 0.4}
                  />
                );
              })}
            </g>
          );
        })}

        <line x1={margin.left} x2={width - margin.right} y1={baseline} y2={baseline} stroke="var(--axis)" strokeWidth={1} />

        {data.map((row, index) => {
          if (!showLabel(index, data.length, innerW)) return null;
          return (
            <text key={`${row.label}-${index}`} x={margin.left + slot * index + slot / 2} y={height - 8} textAnchor="middle" fontSize={10} fill="var(--text-muted)">
              {row.label}
            </text>
          );
        })}
      </svg>

      {hover !== null && (
        <div
          className="tooltip"
          style={{
            left: `${((margin.left + slot * hover + slot / 2) / width) * 100}%`,
            top: legend && keys.length > 1 ? 30 : 0,
            transform: hover > data.length * 0.6 ? 'translate(-105%, 0)' : 'translate(14px, 0)',
          }}
        >
          <div className="tooltip-title">{data[hover].label}</div>
          {[...data[hover].segments]
            .filter((s) => s.value > 0)
            .sort((a, b) => b.value - a.value)
            .map((segment) => (
              <div className="tooltip-row" key={segment.key}>
                <span className="legend-swatch" style={{ background: color(segment.key) }} />
                {segment.label}
                <span className="tooltip-value">{formatValue(segment.value)}</span>
              </div>
            ))}
          <div className="tooltip-row tooltip-total">
            Total
            <span className="tooltip-value">{formatValue(totals[hover])}</span>
          </div>
        </div>
      )}
    </div>
  );
}

export function Sparkline({ values, color = 'var(--accent)', height = 28 }: { values: number[]; color?: string; height?: number }) {
  const gradientId = useId().replace(/:/g, '');
  if (values.length < 2) return <div style={{ height }} />;
  const width = 120;
  const max = Math.max(...values) || 1;
  const pts = values.map((v, i) => [(i / (values.length - 1)) * width, height - 2 - (v / max) * (height - 4)]);
  const line = pts.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  return (
    <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" style={{ width: '100%', height, display: 'block' }} aria-hidden>
      <defs>
        <linearGradient id={gradientId} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity={0.25} />
          <stop offset="100%" stopColor={color} stopOpacity={0} />
        </linearGradient>
      </defs>
      <path d={`${line} L${width},${height} L0,${height} Z`} fill={`url(#${gradientId})`} />
      <path d={line} fill="none" stroke={color} strokeWidth={1.5} vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

/** 100% composition bar. */
export function ShareBar({
  items,
  formatValue = (v) => String(Math.round(v)),
  colorFor,
}: {
  items: Array<{ key: string; label: ReactNode; value: number }>;
  formatValue?: (value: number) => string;
  colorFor?: (key: string, index: number) => string;
}) {
  const total = items.reduce((sum, item) => sum + item.value, 0);
  if (total <= 0) return <div className="chart-empty" style={{ height: 80 }}>Belum ada data</div>;
  const color = (key: string, i: number) => (colorFor ? colorFor(key, i) : seriesColor(i));
  return (
    <div>
      <div className="share-bar">
        {items.map((item, i) =>
          item.value > 0 ? <span key={item.key} style={{ width: `${(item.value / total) * 100}%`, background: color(item.key, i) }} title={`${formatValue(item.value)}`} /> : null,
        )}
      </div>
      <div className="chart-legend" style={{ marginTop: 12 }}>
        {items.map((item, i) => (
          <span className="legend-item" key={item.key}>
            <span className="legend-swatch" style={{ background: color(item.key, i) }} />
            {item.label}
            <span className="muted">{percent((item.value / total) * 100)}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

export function StatTile({
  label,
  value,
  sub,
  tone,
  delta,
  spark,
  accent = false,
}: {
  label: ReactNode;
  value: ReactNode;
  sub?: ReactNode;
  tone?: 'good' | 'warn' | 'critical';
  delta?: ReactNode;
  spark?: number[];
  accent?: boolean;
}) {
  const color =
    tone === 'good' ? 'var(--status-good)' : tone === 'warn' ? 'var(--status-warning)' : tone === 'critical' ? 'var(--status-critical)' : undefined;

  return (
    <div className="stat">
      <div className="stat-label">
        <span>{label}</span>
        {delta}
      </div>
      <div className={accent ? 'stat-value accent' : 'stat-value'} style={color ? { color } : undefined}>
        {value}
      </div>
      {sub !== undefined && <div className="stat-sub">{sub}</div>}
      {spark && (
        <div className="stat-spark">
          <Sparkline values={spark} />
        </div>
      )}
    </div>
  );
}
